import React from "react";
import { DataContext } from "../App";

import LastUpdated from "./LastUpdated";
import Row from "./Row";
import Card from "./Card";
import PestPressure from "./PestPressure";
import PressureContext from "./PressureContext";
import PressureContextLegend from "./PressureContextLegend";
import Forecast from "./Forecast";
import ForecastLegend from "./ForecastLegend";
import SalesBanner from "./SalesBanner";
import Divider from "./Divider";
import AffectedTraps from "./AffectedTraps";
import TotalDetections from "./TotalDetections";
import FruitFlyTraffic from "./FruitFlyTraffic";
import Persistence from "./Persistence";
import Footer from "./Footer";
import DailyChart from "./DailyChart";
import MonthlyFruitFlyTraffic from "./MonthlyFruitFlyTraffic";
import WeatherChart from "./WeatherChart";

function Graphs({ user, region }) {
  const data = React.useContext(DataContext);
  const total_traps = data.historical_total_traps;
  const region_name = data.region_name;

  let allowPremium = false;
  if (user) {
    if (region.allowPremium === 'loggedIn') {
      allowPremium = !!user;
    }
    else if (region.allowPremium === 'cognito') {
      const regions = user.signInUserSession.accessToken.payload["cognito:groups"];
      allowPremium = regions.filter((x) => x.includes(`${region.name}-premium`)).length > 0;
    }
  }

  const topCards = [
    <Card
      key="1"
      cardwidth="3"
      title="Pest pressure trend"
      subtitle="Relative change of the pest pressure"
      child={<PestPressure />}
    />,
    <Card
      key="2"
      cardwidth="6"
      title={
        "Recent and forecasted pest pressure in the " + region_name + " region"
      }
      subtitle={
        "Regional average fruit fly count per trap in the " +
        region_name +
        " region"
      }
      child={<Forecast />}
      legend={<ForecastLegend />}
    />,
    <Card
      key="3"
      cardwidth="3"
      title="Pest pressure context"
      subtitle="Current pest pressure relative to historical values"
      child={<PressureContext />}
      legend={<PressureContextLegend />}
    />,
  ];

  const bottomCards = [
    <Card
      key="1"
      cardwidth="2"
      title="Affected traps"
      subtitle="Traps with detection"
      child={<AffectedTraps />}
    />,
    <Card
      key="2"
      cardwidth="2"
      title="Total detections"
      subtitle={"Sum across all traps in the region"}
      child={<TotalDetections />}
    />,
    <Card
      key="3"
      cardwidth="5"
      title="Fruit fly persistence"
      subtitle="Percentage of traps vs. number of days with detections"
      child={<Persistence />}
    />,
    <Card
      key="4"
      cardwidth="3"
      title="Fruit fly traffic"
      subtitle="Hourly detection distribution"
      child={<FruitFlyTraffic />}
    />,
  ];

  const premiumCards1 = [
    <Card
      key="1"
      cardwidth="6"
      title="Historical maximum single trap count"
      subtitle="Daily maximum count from a single trap"
      child={<DailyChart field="daily_trap_max" type="AreaChart" />}
    />,
    <Card
      key="2"
      cardwidth="6"
      title="Historical pest pressure"
      subtitle="Daily pest pressure values in the region"
      child={<DailyChart field="daily_pest_pressure" type="AreaChart" />}
    />,
  ];

  const premiumCards2 = [
    <Card
      key="1"
      cardwidth="6"
      title="Historical total counts"
      subtitle="Daily total counts in the region"
      child={<DailyChart field="daily_total_counts" type="AreaChart" />}
    />,
    <Card
      key="2"
      cardwidth="6"
      title="Historical affected traps"
      subtitle="Daily percentage of detecting traps in the region"
      child={<DailyChart field="daily_detecting_traps" type="AreaChart" />}
    />,
  ];

  const premiumCards3 = [
    <Card
      key="1"
      cardwidth="6"
      title="Historical weather information"
      subtitle="Temperature (Celsius), Humidity (%) and Rainfall (mm)"
      child={<WeatherChart field="daily_weather" type="LineChart" />}
    />,
    <Card
      key="2"
      cardwidth="6"
      title="Historical monthly fruit fly fraffic"
      subtitle="Hourly detection distribution comparison"
      child={<MonthlyFruitFlyTraffic />}
    />,
  ];

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <LastUpdated/>
          <Row children={topCards} />
          <SalesBanner region={region}/>
          {allowPremium && (
            <>
              <Divider title="Last 14 Days" />
              <Row children={bottomCards} />
              <Divider title="Last 365 Days" />
              <Row children={premiumCards1} />
              <Row children={premiumCards2} />
              <Row children={premiumCards3} />
            </>
          )}
          <Footer />
        </div>
      </div>
    </div>
  );
}

export default Graphs;
